import { useTranslation } from 'next-i18next';
import { Columns2, Grid2x2, Map } from 'lucide-react';
import { buildClassName } from 'utils/build-class-name';
import { Namespaces } from 'types/locales';
import styles from './listings-controls.module.scss';

export type ListingsViewToggleProps = {
  isFullScreen: boolean;
  onSetFullScreen?: (fullScreen: boolean) => void;
  isMapFullscreen: boolean;
  onSetMapFullscreen?: (mapFullscreen: boolean) => void;
  className?: string;
};

export function ListingsViewToggle({
  isFullScreen,
  onSetFullScreen,
  isMapFullscreen,
  onSetMapFullscreen,
  className,
}: ListingsViewToggleProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const mapLabel = isMapFullscreen
    ? t('map.controls.collapse', { defaultValue: 'Collapse map' })
    : t('map.controls.expand', { defaultValue: 'Expand map' });

  return (
    <div className={buildClassName(styles.viewToggle, className)} role="group" aria-label="View">
      <button
        type="button"
        className={buildClassName(styles.viewButton, isMapFullscreen && styles.viewButtonActive)}
        data-testid="AreaPageV2-ViewToggle-Map"
        aria-label={mapLabel}
        aria-pressed={isMapFullscreen}
        onClick={() => onSetMapFullscreen?.(!isMapFullscreen)}
      >
        <Map className={styles.viewIcon} size={14} strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        className={buildClassName(styles.viewButton, !isFullScreen && !isMapFullscreen && styles.viewButtonActive)}
        data-testid="AreaPageV2-ViewToggle-List"
        aria-label="Split view"
        aria-pressed={!isFullScreen && !isMapFullscreen}
        onClick={() => {
          onSetFullScreen?.(false);
          onSetMapFullscreen?.(false);
        }}
      >
        <Columns2 className={styles.viewIcon} size={14} strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        className={buildClassName(styles.viewButton, isFullScreen && !isMapFullscreen && styles.viewButtonActive)}
        data-testid="AreaPageV2-ViewToggle-Grid"
        aria-label="Grid view"
        aria-pressed={isFullScreen && !isMapFullscreen}
        onClick={() => onSetFullScreen?.(true)}
      >
        <Grid2x2 className={styles.viewIcon} size={14} strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}
